import { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatGs, DAYS } from '@/utils/helpers';

export default function PromotionsPage() {
  const [promos, setPromos] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ product_id: '', branch_id: '', day_of_week: 0, promo_price: '', label: '', active: true });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/promociones'),
      api.get('/sucursales'),
      api.get('/productos'),
    ]).then(([pr, br, pd]) => {
      setPromos(pr.data.promociones);
      setBranches(br.data.sucursales);
      setProducts(pd.data.productos);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.product_id || !form.branch_id || !form.promo_price) {
      toast.error('Completá producto, sucursal y precio promo');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/promociones', {
        ...form,
        product_id: parseInt(form.product_id),
        branch_id: parseInt(form.branch_id),
        day_of_week: parseInt(form.day_of_week),
        promo_price: parseInt(form.promo_price),
      });
      toast.success('Promocion creada');
      setShowForm(false);
      setForm({ product_id: '', branch_id: '', day_of_week: 0, promo_price: '', label: '', active: true });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const toggle = async (id, active) => {
    try { await api.put(`/admin/promociones/${id}`, { active }); load(); }
    catch { toast.error('Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar promocion?')) return;
    try { await api.delete(`/admin/promociones/${id}`); toast.success('Eliminada'); load(); }
    catch { toast.error('Error'); }
  };

  // Group promos by branch
  const grouped = {};
  promos.forEach(p => {
    if (!grouped[p.branch_id]) grouped[p.branch_id] = { name: p.branch_name, items: [] };
    grouped[p.branch_id].items.push(p);
  });

  return (
    <div data-testid="admin-promos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Promociones por dia</h2>
          <p className="text-sm text-zinc-500">{promos.length} promociones configuradas</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="promo-add-btn" className="flex items-center gap-2 bg-[#E84C1C] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d43f12] transition-colors">
          <Plus size={16} /> Nueva promo
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-6 space-y-3" data-testid="promo-form">
          <h3 className="text-sm font-semibold">Nueva promocion</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Sucursal</Label>
              <select value={form.branch_id} onChange={e => set('branch_id', e.target.value)} data-testid="promo-branch" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300 mt-1">
                <option value="">Seleccionar...</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Producto</Label>
              <select value={form.product_id} onChange={e => set('product_id', e.target.value)} data-testid="promo-product" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300 mt-1">
                <option value="">Seleccionar...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Dia de la semana</Label>
              <select value={form.day_of_week} onChange={e => set('day_of_week', e.target.value)} data-testid="promo-day" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-300 mt-1">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Precio promo (Gs.)</Label>
              <Input type="number" value={form.promo_price} onChange={e => set('promo_price', e.target.value)} placeholder="35000" data-testid="promo-price" className="bg-zinc-950 border-zinc-800 h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Etiqueta</Label>
              <Input value={form.label} onChange={e => set('label', e.target.value)} placeholder="Promo Lunes" data-testid="promo-label" className="bg-zinc-950 border-zinc-800 h-9 mt-1" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="promo-save" className="flex items-center gap-1 bg-[#E84C1C] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#d43f12] disabled:opacity-50">
            {saving && <Loader2 size={14} className="animate-spin" />} Guardar
          </button>
        </div>
      )}

      {/* Promos list */}
      {loading ? (
        <div className="text-center text-zinc-500 py-8">Cargando...</div>
      ) : promos.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 bg-zinc-900/30 rounded-xl border border-zinc-800/30">
          <p>No hay promociones configuradas</p>
          <p className="text-xs mt-1 text-zinc-600">Crea una nueva promo para un producto en un dia especifico</p>
        </div>
      ) : (
        Object.entries(grouped).map(([branchId, data]) => (
          <div key={branchId} className="mb-6">
            <h3 className="text-sm font-semibold text-zinc-400 mb-2">{data.name}</h3>
            <div className="space-y-2">
              {data.items.map(p => (
                <div key={p.id} data-testid={`promo-row-${p.id}`} className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-3">
                  {p.image_url && <img src={p.image_url.startsWith('http') ? p.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${p.image_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.product_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] bg-[#E84C1C]/10 text-[#E84C1C] px-1.5 py-0.5 rounded-full">{p.label}</span>
                      <span className="text-[10px] text-zinc-500">{DAYS[p.day_of_week]}</span>
                      <span className="text-xs font-bold text-[#E84C1C]">{formatGs(p.promo_price)}</span>
                    </div>
                  </div>
                  <Switch checked={p.active} onCheckedChange={v => toggle(p.id, v)} />
                  <button onClick={() => remove(p.id)} className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
